'use client';

import { useSession } from '@/hooks/use-session';
import { useMemo } from 'react';
import { MenuConfig } from '@/config/types';
import { hasPermission } from '@/lib/permissions';


/**
 * Filter menu items based on user permissions
 */
export function useFilteredMenu(menu: MenuConfig): MenuConfig {
  const { data: session } = useSession();

  return useMemo(() => {
    return filterMenuItems(menu, session);
  }, [menu, session]);
}

/**
 * Recursively filter menu items based on permissions.
 * Only the explicit `permission` field on menu items controls visibility.
 * Page-level permissions (PAGE_PERMISSIONS) are for access control, not navigation display.
 */
function filterMenuItems(items: MenuConfig, session: any): MenuConfig {
  return items
    .map((item) => {
      // If item has an explicit permission requirement, check it
      if (item.permission) {
        if (!hasPermission(session, item.permission)) {
          return null; // Filter out this item
        }
      }

      // requiresAuth: hide the item entirely when no session exists.
      // Distinct from lockedWhenLoggedOut, which keeps the item visible
      // but renders it as a locked/disabled affordance.
      if (item.requiresAuth && !session?.user) {
        return null;
      }

      // Recursively filter children
      let filteredChildren: MenuConfig | undefined;
      if (item.children) {
        filteredChildren = filterMenuItems(item.children, session);
        // If all children are filtered out, filter out parent too (unless it has its own path)
        if (filteredChildren.length === 0 && !item.path) {
          return null;
        }
      }

      // Return filtered item
      return {
        ...item,
        children: filteredChildren,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}
